import { Business, EditOutlined, Person, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  CustomerSearchResult,
  CustomerSearchScope,
  searchCommercialCustomers,
} from "../services/CommercialCustomerSearchService";
import {
  ClientData,
  CommercialAddress,
  CustomerSource,
} from "../types/proposal";
import CommercialAddressFields from "./CommercialAddressFields";

type Props = {
  client: ClientData;
  onChange: <K extends keyof ClientData>(
    field: K,
    value: ClientData[K],
  ) => void;
  onPatch: (data: Partial<ClientData>) => void;
};

const sourceLabels: Record<CustomerSource, string> = {
  manual: "Cadastro manual",
  user: "Usuário Noar",
  asaas: "Cliente Asaas",
};

const ClientCard: React.FC<Props> = ({ client, onChange, onPatch }) => {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<CustomerSearchScope>("all");
  const [results, setResults] = useState<CustomerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");

  const handleSearch = async () => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2) {
      setSearchMessage("Digite pelo menos 2 caracteres para pesquisar.");
      return;
    }

    setSearching(true);
    setSearched(false);
    setSearchMessage("");
    try {
      const response = await searchCommercialCustomers(normalizedQuery, scope);
      setResults(response.results);
      setSearched(true);

      if (response.unavailableSources.length) {
        const unavailable = response.unavailableSources
          .map(source => sourceLabels[source])
          .join(" e ");
        setSearchMessage(`Não foi possível consultar: ${unavailable}.`);
      }
    } catch {
      setResults([]);
      setSearched(true);
      setSearchMessage("Não foi possível realizar a busca agora.");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCustomer = (result: CustomerSearchResult) => {
    onPatch({
      name: result.name,
      company: result.company,
      email: result.email,
      phone: result.phone,
      document: result.document,
      zipCode: result.address.zipCode,
      address: result.address,
      source: result.source,
      userId: result.source === "user" ? result.sourceId : "",
      asaasCustomerId: result.source === "asaas" ? result.sourceId : "",
    });
    setResults([]);
    setSearched(false);
    setSearchMessage("");
  };

  const handleAddressChange = (address: CommercialAddress) => {
    onPatch({ address, zipCode: address.zipCode });
  };

  return (
    <Card>
      <CardHeader
        title="Cliente"
        subheader="Localize um cadastro existente ou preencha os dados manualmente"
        action={
          <Chip
            size="small"
            color={
              client.source && client.source !== "manual" ? "info" : "default"
            }
            label={sourceLabels[client.source ?? "manual"]}
          />
        }
      />
      <CardContent>
        <Box
          sx={{
            p: 2,
            mb: 2.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Buscar cadastro existente
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
            <TextField
              fullWidth
              size="small"
              label="Nome, e-mail ou CPF/CNPJ"
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleSearch();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Buscar em"
              value={scope}
              onChange={event =>
                setScope(event.target.value as CustomerSearchScope)
              }
              sx={{ minWidth: { md: 170 } }}
            >
              <MenuItem value="all">Usuários e Asaas</MenuItem>
              <MenuItem value="user">Somente usuários</MenuItem>
              <MenuItem value="asaas">Somente Asaas</MenuItem>
            </TextField>
            <Button
              variant="contained"
              color="info"
              disabled={searching}
              startIcon={
                searching ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Search />
                )
              }
              onClick={() => void handleSearch()}
              sx={{ minWidth: 120 }}
            >
              Buscar
            </Button>
          </Stack>

          {searchMessage && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              {searchMessage}
            </Alert>
          )}

          {results.length > 0 && (
            <List
              disablePadding
              sx={{
                mt: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1.5,
              }}
            >
              {results.map((result, index) => (
                <React.Fragment key={result.key}>
                  {index > 0 && <Divider />}
                  <ListItemButton onClick={() => handleSelectCustomer(result)}>
                    <ListItemText
                      primary={result.name || "Cliente sem nome"}
                      secondary={[result.email, result.document]
                        .filter(Boolean)
                        .join(" • ")}
                    />
                    <Chip
                      label={sourceLabels[result.source]}
                      color={result.source === "asaas" ? "success" : "info"}
                      variant="outlined"
                      size="small"
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}

          {searched && !searching && results.length === 0 && (
            <Alert
              severity="info"
              icon={<EditOutlined />}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    onPatch({
                      source: "manual",
                      userId: "",
                      asaasCustomerId: "",
                    });
                    setSearched(false);
                  }}
                >
                  Preencher
                </Button>
              }
              sx={{ mt: 1.5 }}
            >
              Nenhum cadastro encontrado. Você pode preencher os dados
              manualmente abaixo.
            </Alert>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome"
              value={client.name}
              onChange={event => onChange("name", event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Empresa"
              value={client.company}
              onChange={event => onChange("company", event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              type="email"
              label="E-mail"
              value={client.email}
              onChange={event => onChange("email", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Telefone"
              placeholder="(00) 00000-0000"
              value={client.phone}
              onChange={event => onChange("phone", event.target.value)}
              inputProps={{ inputMode: "tel" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="CPF/CNPJ"
              value={client.document ?? ""}
              onChange={event => onChange("document", event.target.value)}
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="subtitle2" gutterBottom>
          Endereço do cliente
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O CEP também será usado como destino inicial do frete.
        </Typography>
        <CommercialAddressFields
          address={{
            street: "",
            number: "",
            complement: "",
            neighborhood: "",
            city: "",
            state: "",
            ...client.address,
            zipCode: client.address?.zipCode ?? client.zipCode,
          }}
          onChange={handleAddressChange}
        />
      </CardContent>
    </Card>
  );
};

export default ClientCard;
